import { LegalLayout, Clause } from "./LegalLayout";
import { Database, CreditCard, MapPin, Lock, Ban } from "lucide-react";

const INVENTORY = [
  {
    icon: Database, title: "Personally identifiable information",
    body: "Legal name, work email, home address, Social Security Number, date of hire, employment status, filing status and withholding elections. SSNs are encrypted at rest with Fernet AES-128; only the last four digits are ever rendered in any screen, PDF or API response.",
  },
  {
    icon: CreditCard, title: "Financial and bank data",
    body: "ACH routing number, account number, account type, and the tokenized references returned by our payment processors. Account numbers are encrypted at rest with the same AES-128 scheme as SSNs. We never store a full card number, CVC, or expiry — card data is captured inside Stripe's own iframe and reaches us only as a payment-method token.",
  },
  {
    icon: MapPin, title: "Location and device data",
    body: "Clock-in GPS coordinates, captured only at the moment you tap Clock In, used solely for geofence validation. Portal-login IP address and coarse city/country, captured only when your employer has enabled Remote Work Tracking and you have accepted that specific disclosure.",
  },
];

export default function LegalPrivacy() {
  return (
    <LegalLayout eyebrow="LEGAL · PRIVACY POLICY" title="Privacy Policy" updated="2026-06-01" active="privacy">
      <Clause n="1" heading="What we collect">
        <p>Checkmate Payroll collects only what is required to run payroll, meet tax obligations and keep your account secure. The complete inventory:</p>
        <ul className="space-y-3 mt-3">
          {INVENTORY.map((row) => (
            <li key={row.title} className="border-2 border-ink p-3 flex gap-3" data-testid={`privacy-inventory-${row.title.split(" ")[0].toLowerCase()}`}>
              <row.icon className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <b>{row.title}</b>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{row.body}</div>
              </div>
            </li>
          ))}
        </ul>
      </Clause>

      <Clause n="2" heading="Financial processing — Stripe and Plaid">
        <p>
          Checkmate is not a bank and does not hold your money. Movement of funds is performed by two regulated
          processors, and each receives the minimum data required to complete the instruction:
        </p>
        <ul className="list-disc pl-6 space-y-1" data-testid="privacy-processors">
          <li>
            <b>Stripe, Inc.</b> — payment processing, Stripe Connect payouts and instant payouts. Stripe receives payout
            amounts, the destination account or payment-method token, and the payroll reference. Business debit cards are
            entered directly into Stripe Elements: the card number never touches Checkmate's servers, logs or database,
            and we retain only Stripe's <code>pm_…</code> token plus the brand and last four digits. Stripe processes this
            data as an independent controller under its own privacy policy and PCI-DSS Level 1 attestation.
          </li>
          <li>
            <b>Plaid Inc.</b> — bank-account verification and ACH pre-funding. When you choose to link a bank account,
            Plaid authenticates you directly with your financial institution; Checkmate never sees or stores your online
            banking username or password. Plaid returns an item identifier, an access token and the verified routing and
            account numbers. The access token is encrypted at rest and is never returned to any browser or API client.
          </li>
        </ul>
        <p>
          You may decline both. Businesses can fund payroll by check or manual ACH, and employees can receive a physical
          check instead of a direct deposit, at the cost of the speed those processors provide.
        </p>
      </Clause>

      <Clause n="3" heading="Encryption and security posture">
        <div className="border-2 border-ink p-4 flex gap-3 bg-secondary/40" data-testid="privacy-encryption">
          <Lock className="w-6 h-6 shrink-0" />
          <div>
            <div className="font-black">Bank-grade encryption on every sensitive field</div>
            <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc pl-4">
              <li>Social Security Numbers: encrypted at rest (Fernet AES-128, HMAC-authenticated). Last four digits only in the UI.</li>
              <li>Bank routing and account numbers: encrypted at rest with the same scheme. Decrypted only in the moment a payout instruction is built.</li>
              <li>Card data: never stored. Collected inside Stripe's hosted iframe; we hold only a processor token.</li>
              <li>Plaid access tokens: encrypted at rest and excluded from every API response.</li>
              <li>Transport: TLS in transit on every request. Session cookies are HttpOnly and SameSite=Lax.</li>
              <li>Access: administrative actions are written to an immutable audit log with actor, timestamp and changed fields.</li>
            </ul>
          </div>
        </div>
      </Clause>

      <Clause n="4" heading="We do not sell your data">
        <div className="border-2 border-bad bg-bad/10 p-4 flex gap-3" data-testid="privacy-no-sale">
          <Ban className="w-6 h-6 shrink-0 text-bad" />
          <div className="leading-relaxed">
            <div className="font-black">Absolute prohibition on sale, rental or monetization.</div>
            Checkmate does not sell, rent, license, trade or otherwise monetize personal information, payroll data,
            bank data or location data — to anyone, at any price, under any circumstance. We run no advertising
            network, embed no third-party ad or analytics trackers, and do not build marketing profiles. We do not
            share data with data brokers. Personal information is disclosed only to the processors named in section 2,
            to your own employer of record, and to a government authority holding a valid legal demand. This prohibition
            survives any acquisition of Checkmate: a successor is bound by this policy or must obtain fresh consent.
          </div>
        </div>
      </Clause>

      <Clause n="5" heading="Other service providers">
        <ul className="list-disc pl-6">
          <li><b>Twilio</b> — phone number, for SMS alerts you have opted into.</li>
          <li><b>Resend</b> — email address, to deliver pay-stub and workflow notifications.</li>
          <li><b>Mapbox</b> — worksite latitude and longitude for geocoding; no personal identifiers.</li>
          <li><b>ipapi.co</b> — public IP address only, and only under Remote Work Tracking.</li>
          <li><b>AI providers</b> — anonymized aggregate signals for the Office Manager, Billing Concierge and Buddy chat. AI models are never given SSNs, bank details or full addresses.</li>
        </ul>
      </Clause>

      <Clause n="6" heading="How long we keep it">
        <p>
          Payroll records are retained for seven years to satisfy IRS, FLSA and state requirements, then flagged for
          reviewed deletion. The full schedule, including what is anonymized versus purged, is published in our{" "}
          <a href="/legal/retention" className="underline">Data Retention Policy</a>.
        </p>
      </Clause>

      <Clause n="7" heading="Your rights">
        <ul className="list-disc pl-6">
          <li><b>Access and portability</b> — Settings → Right to be Forgotten → Download My Data returns a machine-readable dump of every record we hold about you.</li>
          <li><b>Deletion</b> — Delete My Account removes your login and employee profile. Payroll history your employer is legally required to keep is anonymized so it no longer identifies you.</li>
          <li><b>Correction</b> — ask your payroll administrator, or email us directly, to fix inaccurate data.</li>
          <li><b>Opt-out of sale</b> — not applicable: there is no sale to opt out of (see section 4).</li>
        </ul>
        <p>Rights under GDPR Art. 15–17 and CCPA §1798.100–.125 are honoured for all users regardless of residence. Requests are completed within 30 days.</p>
      </Clause>

      <Clause n="8" heading="Contact">
        <p>Privacy questions and data-subject requests: <b>privacy@checkmatepayrolls.com</b>. Security disclosures: <b>legal@checkmatepayrolls.com</b>.</p>
      </Clause>
    </LegalLayout>
  );
}
